import { DEBUG, DEBUG_ALERT_MSG } from '../config';
import { isCurrentTabMaster } from './establishMultitabRole';
import { throttle } from './schedulers';

const SHOULD_SHOW_DEBUG_ALERT = false;

let showError = true;
let error: Error | undefined;

window.addEventListener('error', handleErrorEvent);
window.addEventListener('unhandledrejection', handleErrorEvent);

if (DEBUG && SHOULD_SHOW_DEBUG_ALERT) {
  window.addEventListener('focus', () => {
    if (!isCurrentTabMaster()) {
      return;
    }
    showError = true;
    if (error) {
      // eslint-disable-next-line no-alert
      window.alert(getErrorMessage(error));
      error = undefined;
    }
  });
  window.addEventListener('blur', () => {
    if (!isCurrentTabMaster()) {
      return;
    }
    showError = false;
  });
}

const throttleError = throttle((err) => {
  if (!SHOULD_SHOW_DEBUG_ALERT) {
    return;
  }

  if (showError) {
    // eslint-disable-next-line no-alert
    window.alert(getErrorMessage(err));
  } else {
    error = err;
  }
}, 1500);

/** 支持 Error 实例或带 message 的普通对象（如 Worker 返回的 { message }） */
export function handleError(err: Error | { message?: string } | unknown) {
  // eslint-disable-next-line no-console
  console.error(err);
  
  // 尝试自动恢复缓存错误
  if (err instanceof Error) {
    import('./cacheRecovery')
      .then(({ detectAndRecoverCacheError }) => detectAndRecoverCacheError(err))
      .then((recovered) => {
        if (recovered) {
          // 已自动恢复，不再显示错误
          return;
        }

        // 未恢复，继续正常错误处理
        showErrorIfDebug(err);
      })
      .catch(() => {
        // 恢复失败，继续正常错误处理
        showErrorIfDebug(err);
      });
  } else {
    showErrorIfDebug(err);
  }
}

function showErrorIfDebug(err: Error | { message?: string } | unknown) {
  if (DEBUG) {
    const message = typeof err === 'object' && err !== null && 'message' in err
      ? String((err as { message?: unknown }).message)
      : undefined;
    if (
      typeof message === 'string'
      && (
        message.includes('STICKERSET_INVALID')
        || message.includes('Could not find a matching Constructor ID')
      )
    ) {
      return;
    }
    throttleError(err);
  }
}

function handleErrorEvent(e: ErrorEvent | PromiseRejectionEvent) {
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  if (e instanceof ErrorEvent && e.message === 'ResizeObserver loop limit exceeded') {
    return;
  }

  e.preventDefault();
  handleError(e instanceof ErrorEvent ? (e.error || e.message) : e.reason);
}

/** 统一把各类错误转为可读字符串，避免 [object Object] */
function getErrorMessage(err: Error | { message?: string } | unknown): string {
  if (err == null) {
    return DEBUG_ALERT_MSG;
  }
  if (err instanceof Error) {
    return `${DEBUG_ALERT_MSG}\n\n${err.message}\n${err.stack ?? ''}`;
  }
  if (typeof err === 'object' && 'message' in err) {
    const msg = (err as { message?: unknown }).message;
    return `${DEBUG_ALERT_MSG}\n\n${typeof msg === 'string' ? msg : JSON.stringify(err)}`;
  }
  return `${DEBUG_ALERT_MSG}\n\n${typeof err === 'string' ? err : JSON.stringify(err)}`;
}
